import { useEffect, useRef } from "react";
import QRCodeStyling from "qr-code-styling";

type Props = {
  value: string;
  size: number;
  dotsType?: "dots" | "classy-rounded";
  errorCorrectionLevel?: "L" | "M" | "Q" | "H";
  showSkeleton?: boolean;
};

export default function QRCode({
  value,
  size,
  dotsType = "dots",
  errorCorrectionLevel = "Q",
  showSkeleton = true,
}: Props) {
  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const qrStyledInstanceRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!qrCodeRef.current || !value) return;

    // Clear existing QR content
    qrCodeRef.current.innerHTML = "";

    const qrWrapper = qrCodeRef.current;
    qrWrapper.style.position = "relative";

    qrStyledInstanceRef.current = new QRCodeStyling({
      width: size,
      height: size,
      type: "svg",
      data: value,
      dotsOptions: {
        type: dotsType,
        color: "#000000",
      },
      cornersSquareOptions: {
        type: "extra-rounded",
        color: "#000000",
      },
      cornersDotOptions: {
        type: "extra-rounded",
        color: "#000000",
      },
      backgroundOptions: {
        color: "#ffffff",
        round: 0,
      },
      qrOptions: {
        errorCorrectionLevel,
        typeNumber: 0,
      },
      margin: 0,
      shape: "square",
    });

    qrStyledInstanceRef.current.append(qrWrapper);

    // Style the QR element to fill container properly
    // Use requestAnimationFrame to ensure SVG is fully rendered
    requestAnimationFrame(() => {
      const svgElement = qrWrapper.firstElementChild as SVGElement;

      if (svgElement && svgElement.tagName.toLowerCase() === "svg") {
        // Remove fixed width/height attributes to allow CSS stretching
        svgElement.removeAttribute("width");
        svgElement.removeAttribute("height");

        // Apply 100% width/height via CSS
        svgElement.style.width = "100%";
        svgElement.style.height = "100%";
        svgElement.style.display = "block";
      }
    });
  }, [value, size, dotsType, errorCorrectionLevel]);

  return (
    <div className="relative w-full h-full">
      <div ref={qrCodeRef} className="relative w-full h-full" />

      {showSkeleton && !value && (
        <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg overflow-hidden">
          <div className="grid grid-cols-6 grid-rows-6 gap-1 w-full h-full p-3">
            {Array.from({ length: 36 }).map((_, i) => (
              <div
                key={i}
                className="bg-gray-200 rounded-sm"
                style={{
                  opacity: Math.random() * 0.3 + 0.1,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
